import { loadStripe } from '@stripe/stripe-js';
import { MetodoPagamentoVendaEnum } from '../types/enums/metodo-pagamento.enum';
import { Checkout } from '../types/checkout';
import { CreatePurchaseContract } from '../types/create-purchase-contract';
import { removeMask } from '../utils/remove-mask';
import { getAllUrlParams, getUrlParams } from '../utils/get-url-params';
import { Api } from '../hooks/useApi';
import { emitUtmifyEvent } from '../utils/emitUtmifyEvent';
import { CreatePixPurchaseContract } from '../types/create-credit-card-puchase-contract';
import { getStripePublicKey } from '../utils/stripe-public-key';

type AmazonPayCtx = {
  data: CreatePurchaseContract;
  checkout: Checkout;
  id?: string;
  api: Api;
  gerarTokenRecaptcha: (path: string) => Promise<string>;
  navigate: (path: string, options?: any) => void;
  location: any;
  getUrlParams: typeof getUrlParams;
  paymentMethod: MetodoPagamentoVendaEnum;
  alert: any;
  dispatch: any;
};

export const amazonPayStrategy = async (rawCtx: any): Promise<void> => {
  const ctx = rawCtx as AmazonPayCtx;
  const { data, checkout, api, alert, gerarTokenRecaptcha, id } = ctx;
  const afiliadoId = getUrlParams('affiliate') ?? undefined;

  const text = (value?: string) => (typeof value === 'string' ? value.trim() : '');
  const digits = (value?: string) => removeMask(text(value));
  const buyerEmail = text(data?.email);
  const buyerName = text(data?.fullName);
  const buyerPhone = digits(data?.phoneNumber);
  const buyerCpf = digits(data?.cpf);

  try {
    const recaptchaToken = await gerarTokenRecaptcha('/venda');

    const payload: Partial<CreatePixPurchaseContract> &
      Pick<
        CreatePixPurchaseContract,
        'metodoPagamento' | 'produtoId' | 'checkoutId' | 'recaptchaToken'
      > = {
      metodoPagamento: MetodoPagamentoVendaEnum.AMAZON_PAY,
      produtoId: id ?? checkout?.produtoId ?? '',
      checkoutId: checkout?.id ?? '',
      recaptchaToken,
      afiliadoId,
      utmSource: getUrlParams('utm_source'),
      utmCampaign: getUrlParams('utm_campaign'),
      utmMedium: getUrlParams('utm_medium'),
      utmContent: getUrlParams('utm_content'),
      utmTerm: getUrlParams('utm_term'),
      sck: getUrlParams('sck'),
      src: getUrlParams('src'),
      urlParams: getAllUrlParams(),
      ...(buyerEmail ? { emailComprador: buyerEmail } : {}),
      ...(buyerName
        ? { nomeComprador: buyerName, sobrenomeComprador: buyerName }
        : {}),
      ...(buyerCpf ? { cpfComprador: buyerCpf } : {}),
      ...(buyerPhone ? { telefoneComprador: buyerPhone } : {}),
      ...(text(data?.postalCode) ? { postalCode: text(data?.postalCode) } : {}),
      ...(text(data?.state) ? { state: text(data?.state) } : {}),
      ...(text(data?.city) ? { city: text(data?.city) } : {}),
      ...(text(data?.neighborhood)
        ? { district: text(data?.neighborhood) }
        : {}),
      ...(text(data?.address) ? { address: text(data?.address) } : {}),
      ...(text(data?.number) ? { number: text(data?.number) } : {}),
      ...(text(data?.complement) ? { complement: text(data?.complement) } : {}),
    };

    const res = await api.Venda.createPix(
      payload as unknown as CreatePixPurchaseContract,
    );

    if (res.error || !res.data) {
      const msg = Array.isArray(res.message)
        ? (Array.isArray(res.message[0]) ? res.message[0][0] : res.message[0])
        : 'Não foi possível iniciar o pagamento com Amazon Pay.';
      alert.show(msg);
      return;
    }

    const venda = res.data;

    emitUtmifyEvent({
      event: 'waiting_payment',
      vendaId: venda?.id,
    });

    const clientSecret: string | undefined = venda?.clientSecret;
    if (!clientSecret) {
      alert.show('Não foi possível iniciar o pagamento com Amazon Pay.');
      return;
    }

    const stripe = await loadStripe(getStripePublicKey(checkout));
    if (!stripe) {
      alert.show('Stripe não foi carregado corretamente.');
      return;
    }

    const snapshotKey = venda?.id ? `amazonpay:return:${venda.id}` : null;
    if (snapshotKey) {
      try {
        sessionStorage.setItem(
          snapshotKey,
          JSON.stringify({
            purchase: res,
            checkout,
            contract: data,
          }),
        );
      } catch (storageErr) {
        console.warn('[AMAZON_PAY] Falha ao salvar snapshot de retorno', storageErr);
      }
    }

    const returnParams = new URLSearchParams();
    if (venda?.id) returnParams.set('vendaId', venda.id);
    if (checkout?.id) returnParams.set('checkoutId', checkout.id);
    if (venda?.vendaToken) returnParams.set('vendaToken', venda.vendaToken);
    returnParams.set('redirect_source', 'amazonpay');

    const returnUrl = `${window.location.origin}/venda/amazon-pay-return?${returnParams.toString()}`;
    const result = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: { type: 'amazon_pay' },
      } as Parameters<typeof stripe.confirmPayment>[0]['confirmParams'],
    });

    if (result?.error) {
      emitUtmifyEvent({
        event: 'refused',
        vendaId: venda?.id,
      });
      alert.show(
        result.error.message || 'Erro ao processar pagamento com Amazon Pay.',
      );
    }
  } catch (err: any) {
    console.error('[AMAZON_PAY]', err);
    const apiMessage = err?.response?.data?.message;
    const firstMessage = Array.isArray(apiMessage)
      ? Array.isArray(apiMessage[0])
        ? apiMessage[0][0]
        : apiMessage[0]
      : undefined;
    alert.show(
      firstMessage ||
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro inesperado ao iniciar o fluxo de pagamento com Amazon Pay.',
    );
  }
};
